import React, { useState } from 'react';
import "./Admin.css";
import { Link, useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { cardApi } from '../api/card';
import PageHeader from './PageHeader';
import FilePicker from './FilePicker';
import BatchCardUpload from './BatchCardUpload';
import { notify } from "../utils/notice";

const language = "japanese";
type EncodingType = 'utf-8' | 'shift-jis';

const Admin: React.FC = () => {
  const navigate = useNavigate();

  const [encoding, setEncoding] = useState<EncodingType>('utf-8');
  // 画像を含めるか・取り込み方法は画面に出していない（旧来も非表示だった）ので固定値
  const [exportImages] = useState(false);
  const [dataMode] = useState<string>('update');
  const [imageMode] = useState<string>('modify');
  const [file, setFile] = useState<File | null>(null);

  const handleExportCards = async () => {
    try {
      const userKey = 2;
      const token = '7e16b5527c77ea58';
      const response = await cardApi.exportCards(userKey, token, exportImages, encoding);
  
      // Create a download link for the received file
      const url = window.URL.createObjectURL(response);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'cards_export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Failed to export cards:', error);
      notify('名刺情報のエクスポート時にエラーが発生しました。', 'エラー');
    }
  };

  const handleImportCards = async () => {
    const userKey = 2;
    const token = '7e16b5527c77ea58';

    if (!file) {
      notify('Excelファイルを選択してください。');
      return;
    }

    try {
      const reader = new FileReader();
      reader.onload = async (event) => {
        const fileData = event.target?.result;
        if (!fileData) {
          notify('ファイルの読み込みに失敗しました。', 'エラー');
          return;
        }

        let csv = '';
        if (file.name.endsWith('.csv')) {
          csv = fileData as string;
        } else if (file.name.endsWith('.xlsx')) {
          if (typeof fileData === 'string') {
            notify('ファイルのフォーマットに誤りがあります。正しいフォーマットのファイルを選択してください。', 'エラー');
            return;
          }
          const workbook = XLSX.read(new Uint8Array(fileData), { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const sheet = workbook.Sheets[sheetName];
          csv = XLSX.utils.sheet_to_csv(sheet);
        } else {
          notify('ファイル形式に誤りがあります。Excel形式のファイルを選択してください。', 'エラー');
          return;
        }

        try {
          await cardApi.importCards(userKey, token, dataMode, imageMode, csv);
          notify('名刺情報が正しくインポートされました。', '完了');
        } catch (error) {
          console.error('Failed to import cards:', error);
          notify('ファイルのインポート時にエラーが発生しました。', 'エラー');
        }
      };
      reader.onerror = () => {
        notify('ファイルの読み込みに失敗しました。', 'エラー');
      };
      if (file.name.endsWith('.csv')) {
        reader.readAsText(file);
      } else {
        reader.readAsArrayBuffer(file); // for Excel files
      }
    } catch (error) {
      console.error('Error reading file:', error);
      notify('ファイルの読み込み時にエラーが発生しました。', 'エラー');
    }
  };

  const linkData = [
    { to: "/logs", english: "Logs", japanese: "閲覧履歴を開く" },
//    { to: "/users", english: "Users", japanese: "ユーザー" },
//    { to: "/tags", english: "Tags", japanese: "タグ" },
  ];

  // 各機能を名刺トップと同じ「見出し帯つきの箱」に分ける（2026-11-02）
  const box = (title: string, body: React.ReactNode) => (
    <section className="drop-shadow-md">
      <div className="bg-primary-gray rounded-t-md">
        <h2 className="c-box__title">{title}</h2>
      </div>
      <div className="bg-white rounded-b-md c-box__body adminBox__body">{body}</div>
    </section>
  );

  const linkElements = (
    linkData.map((link, index) => (
      <Link key={index} to={link.to} className="btn btn-secondary adminBox__button">
        {language === "japanese" ? link.japanese : link.english}
      </Link>
    ))
  );

  const exportElements = (
    <div className="adminBox__row">
      <select value={encoding} onChange={(e) => setEncoding(e.target.value as EncodingType)} className="c-input adminBox__select" aria-label="文字コード">
        <option value="utf-8">UTF-8</option>
        <option value="shift-jis">Shift-JIS</option>
      </select>
      <button type="button" className="btn btn-primary" onClick={handleExportCards}>
        {language === "japanese" ? "CSVエクスポート" : "Export Card CSV"}
      </button>
    </div>
  );

  const importElements = (
    <>
      <FilePicker
        accept=".csv, .xlsx"
        selectedText={file ? file.name : null}
        onChange={(e) => setFile(e.target.files ? e.target.files[0] : null)}
      />
      <div>
        <button type="button" className="btn btn-primary" disabled={!file} onClick={handleImportCards}>
          {language === "japanese" ? "名刺データインポート" : "Import Cards"}
        </button>
      </div>
    </>
  );

  return (
    <div className="top-page-container">
      <PageHeader title="管理機能" showBack />
      <div className="pageBody adminBoxes">
        {box("閲覧履歴", linkElements)}
        {box("名刺データの書き出し（CSV）", exportElements)}
        {box("名刺データの取り込み（CSV・Excel）", importElements)}
        {box("名刺画像の一括取り込み", <BatchCardUpload />)}
      </div>
    </div>
  );
};

export default Admin;
